export const PC_OBJECT_ATTR = 'data-pc-object';
export const PC_TYPE_ATTR = 'data-pc-type';
export const PC_EDITABLE_ATTR = 'data-pc-editable';
export const PC_LOCK_ATTR = 'data-pc-lock';
export const PC_CHART_SPEC_ATTR = 'data-pc-chart-spec';
export const PC_TEXT_FRAME_ATTR = 'data-pitchcrow-text-frame';
export const EDITOR_ELEMENT_ID_ATTR = 'data-id';
const EDITOR_SELECTED_ATTR = 'data-pitchcrow-selected';
export const EDITOR_HANDLE_ATTR = 'data-pitchcrow-editor-handle';

export const PC_PRIMITIVE_TYPES = [
  'text',
  'shape',
  'image',
  'table',
  'chart',
  'group',
] as const;

export type PcPrimitiveType = (typeof PC_PRIMITIVE_TYPES)[number];

const PC_PRIMITIVE_TYPE_SET = new Set<string>(PC_PRIMITIVE_TYPES);

export const EDITOR_EDITABLE_CANDIDATE_SELECTOR = [
  `[${PC_EDITABLE_ATTR}="true"]`,
  `[${PC_TYPE_ATTR}]`,
  `[${PC_TEXT_FRAME_ATTR}="true"]`,
  `[${PC_CHART_SPEC_ATTR}]`,
  'table',
  'img',
].join(',');

type EditorContractElement = {
  readonly tagName: string;
  getAttribute(name: string): string | null;
  hasAttribute(name: string): boolean;
};

function normalizePcType(value: string | null | undefined) {
  return value && PC_PRIMITIVE_TYPE_SET.has(value)
    ? (value as PcPrimitiveType)
    : null;
}

export function inferPcType(element: EditorContractElement): PcPrimitiveType {
  const explicit = normalizePcType(element.getAttribute(PC_TYPE_ATTR));
  if (explicit) return explicit;

  const tagName = element.tagName.toLowerCase();
  if (element.hasAttribute(PC_CHART_SPEC_ATTR)) return 'chart';
  if (tagName === 'table') return 'table';
  if (tagName === 'img') return 'image';
  if (element.hasAttribute(PC_TEXT_FRAME_ATTR)) return 'text';
  return tagName === 'svg' ? 'shape' : 'group';
}

// Editing-session artifacts (selection markers, TinyMCE internals, transient
// inline styles) stripped from slide HTML before persistence or diffing.
// The dashboard iframe serializer, the deck command sanitizer, and the
// dashboard slide-update sanitizer all consume these same lists: optimistic
// client HTML and persisted server HTML must converge on the same string for
// echo suppression to work.
// `role` and the blanket `aria-` prefix are deliberately NOT stripped:
// agent-authored accessibility metadata (aria-label, role="img") must
// survive persistence. Only TinyMCE's enumerated artifacts are removed.
export const EPHEMERAL_EDITOR_ATTRS = [
  EDITOR_SELECTED_ATTR,
  EDITOR_HANDLE_ATTR,
  'contenteditable',
  'spellcheck',
  'aria-multiline',
  'tabindex',
] as const;

export const EPHEMERAL_EDITOR_ATTR_PREFIXES = ['data-mce-'] as const;

export const EPHEMERAL_EDITOR_CLASS_NAMES = [
  'mce-content-body',
  'mce-edit-focus',
] as const;

export const EPHEMERAL_EDITOR_STYLE_PROPS = [
  'user-select',
  'white-space',
  'word-break',
] as const;
